import React from 'react';
import { formatLongWeekdayMonthDayYear, format12HourTime } from 'shared/Dates';
import { Avatar, Link, Row, Table, TableColumn, Text } from 'library/ui';

import { hoursReported, actions, needsReview } from './utils';

import type { TimesheetWorker, UnclockedInTimesheetWorker } from 'parent-portal/home/new/types';
import { CenterMissingPayment } from 'models';

type TimesheetItem = TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment;
type TimesheetRow = { id: string; item: TimesheetItem };

export function LargeTimesheetsTable({
    data,
    editAction,
}: {
    data: TimesheetWorker[] | UnclockedInTimesheetWorker[] | CenterMissingPayment[];
    editAction: (timesheet: TimesheetItem) => void;
}) {
    const rows: TimesheetRow[] = data.map((t) => ({
        id: `${t.user.id}-${t.start_date}`,
        item: t,
    }));

    const columns: TableColumn<TimesheetRow>[] = [
        {
            key: 'name',
            label: 'Name',
            render: ({ item }) => (
                <Row gap="sm">
                    <Avatar
                        imageUrl={item.profile_image?.image}
                        initials={`${item.user.first_name[0]}${item.user.last_name[0]}`}
                    />
                    <Link to={`/provider/${item.user.id}`} size="sm">
                        {item.user.first_name} {item.user.last_name}
                    </Link>
                    {needsReview(item)}
                </Row>
            ),
        },
        {
            key: 'scheduled',
            label: 'Shift Scheduled',
            render: ({ item }) =>
                item.end_date ? (
                    <>
                        <Text size="sm" component="div">
                            {formatLongWeekdayMonthDayYear(item.start_date, item.timezone)}
                        </Text>
                        <Text size="sm" component="div">
                            {format12HourTime(item.start_date, item.timezone)} -{' '}
                            {format12HourTime(item.end_date, item.timezone, true)}
                        </Text>
                    </>
                ) : (
                    <Text size="sm" component="div">
                        No shift schedule found
                    </Text>
                ),
        },
        {
            key: 'reported',
            label: 'Hours Reported',
            render: ({ item }) => {
                const text = hoursReported(item);
                return (
                    <>
                        {text.middle}
                        {text?.bottom ?? null}
                    </>
                );
            },
        },
        {
            key: 'actions',
            label: 'Actions',
            render: ({ item }) => actions(item, editAction),
        },
    ];

    return <Table columns={columns} data={rows} />;
}
